// ** Redux Imports
import { createSlice } from '@reduxjs/toolkit'

// ** LocalStorage key for persistence
const STORAGE_KEY = 'providerOnboardingProgress'
const STORAGE_EXPIRY_DAYS = 30 // Data expires after 30 days

// ** Helper function to load from localStorage
const loadFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored)
    
    // Check if data has expired
    if (parsed.expiresAt && new Date().getTime() > parsed.expiresAt) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    return parsed
  } catch (error) {
    console.error('Error loading provider onboarding progress:', error)
    return null
  }
}

// ** Helper function to save to localStorage
const saveToLocalStorage = (state) => {
  try {
    const expiresAt = new Date().getTime() + (STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    const dataToStore = {
      ...state,
      expiresAt,
      lastSaved: new Date().toISOString()
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore))
  } catch (error) {
    console.error('Error saving provider onboarding progress:', error)
  }
}

// ** Initial state
const getInitialState = () => {
  const savedState = loadFromLocalStorage()
  
  if (savedState) {
    return {
      currentStep: savedState.currentStep || 1,
      formData: savedState.formData || {},
      contextData: savedState.contextData || null,
      hasProgress: true,
      lastSaved: savedState.lastSaved || null
    }
  }

  return {
    currentStep: 1,
    formData: {},
    contextData: null,
    hasProgress: false,
    lastSaved: null
  }
}

// ** Redux Slice
export const providerOnboardingSlice = createSlice({
  name: 'providerOnboarding',
  initialState: getInitialState(),
  reducers: {
    // Save current progress
    saveProgress: (state, action) => {
      const { currentStep, formData, contextData } = action.payload

      if (currentStep !== undefined) state.currentStep = currentStep
      if (formData !== undefined) {
        // Only update if formData is different
        state.formData = formData
      }
      if (contextData !== undefined) state.contextData = contextData

      state.hasProgress = true
      state.lastSaved = new Date().toISOString()

      // Persist to localStorage (this doesn't trigger re-render)
      saveToLocalStorage(state)
    },

    // Update only the current step
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload
      state.lastSaved = new Date().toISOString()
      saveToLocalStorage(state)
    },

    // Update form data for a specific step
    updateStepData: (state, action) => {
      const { step, data } = action.payload
      state.formData[step] = { ...state.formData[step], ...data }
      state.lastSaved = new Date().toISOString()
      saveToLocalStorage(state)
    },

    // Set context data
    setContextData: (state, action) => {
      state.contextData = action.payload
      state.lastSaved = new Date().toISOString()
      saveToLocalStorage(state)
    },

    // Load saved progress (called on component mount)
    loadProgress: (state) => {
      const savedState = loadFromLocalStorage()
      if (savedState) {
        state.currentStep = savedState.currentStep || 1
        state.formData = savedState.formData || {}
        state.contextData = savedState.contextData || null
        state.hasProgress = true
        state.lastSaved = savedState.lastSaved || null
      }
    },

    // Clear all progress
    clearProgress: (state) => {
      state.currentStep = 1
      state.formData = {}
      state.contextData = null
      state.hasProgress = false
      state.lastSaved = null
      
      // Remove from localStorage
      localStorage.removeItem(STORAGE_KEY)
    },

    // Reset to initial state (used after successful submission)
    resetOnboarding: (state) => {
      Object.assign(state, {
        currentStep: 1,
        formData: {},
        contextData: null,
        hasProgress: false,
        lastSaved: null
      })
      localStorage.removeItem(STORAGE_KEY)
    }
  }
})

// ** Export actions
export const {
  saveProgress,
  setCurrentStep,
  updateStepData,
  setContextData,
  loadProgress,
  clearProgress,
  resetOnboarding
} = providerOnboardingSlice.actions

// ** Export reducer
export default providerOnboardingSlice.reducer

